import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import axios from "axios";
import toast from "react-hot-toast";
import CustomFilterInput from "../../shared/CustomFilterInput";
import AppButton from "../../shared/AppButton";
import AppTable from "../../shared/AppTable";

const ReportSummary = () => {
  const token = localStorage.getItem("token");
  const tokenWithoutQuotes = token ? token.replace(/"/g, "") : "";
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const payersData = localStorage.getItem("payersData");
  const payerDataOptions = payersData ? JSON.parse(payersData) : [];

  const [reportPayload, setReportPayload] = useState({
    payer_id: "",
    start_date: "",
    end_date: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReportPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGetReports = async () => {
    try {
      if (
        !reportPayload?.payer_id ||
        !reportPayload?.start_date ||
        !reportPayload?.end_date
      ) {
        toast.error(t("Please select all the required fields"));
        return;
      }
      setIsLoading(true);
      const response = await axios.post(
        "https://stage.api.connect.supergitsa.com/api/getReport",
        reportPayload,
        {
          headers: {
            Authorization: `Bearer ${tokenWithoutQuotes}`,
          },
        },
      );
      setReportData(response.data);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toast.error(t("Error fetching report data"));
    }
  };

  const getMonthlyColumns = () => [
    {
      header: t("Outcome"),
      render: (row) => {
        if (row.isTotal)
          return <span className="font-bold text-gray-800">{t("Total")}</span>;

        const val = row.outcome || "";
        let theme = "bg-gray-100 text-gray-600";
        const lowerVal = val.toLowerCase();

        if (
          lowerVal === "complete" ||
          lowerVal.includes("accept") ||
          lowerVal.includes("approve")
        )
          theme = "bg-green-100 text-green-700";
        else if (lowerVal.includes("reject") || lowerVal.includes("deny"))
          theme = "bg-red-100 text-red-700";
        else if (lowerVal.includes("partial"))
          theme = "bg-blue-100 text-blue-700";
        else if (lowerVal === "queued") theme = "bg-purple-100 text-purple-700";

        return (
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${theme}`}
          >
            {val}
          </span>
        );
      },
    },
    {
      header: t("Status"),
      render: (row) => {
        if (row.isTotal) return "";
        const val = row.status || "N/A";
        let theme = "bg-gray-100 text-gray-600";
        const lowerVal = val.toLowerCase();

        if (lowerVal === "approved" || lowerVal === "active")
          theme = "bg-green-100 text-green-700";
        else if (lowerVal.includes("pended") || lowerVal.includes("waiting"))
          theme = "bg-amber-100 text-amber-700";
        else if (lowerVal.includes("queued"))
          theme = "bg-purple-100 text-purple-700";
        else if (lowerVal.includes("reject")) theme = "bg-red-100 text-red-700";

        return (
          <span
            className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${theme}`}
          >
            {val}
          </span>
        );
      },
    },
    {
      header: t("Submitted"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.submitted_value}
        </span>
      ),
    },
    {
      header: t("Approved"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.approved_value}
        </span>
      ),
    },
    {
      header: t("Payer (w/ Tax)"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.payer_share_with_tax}
        </span>
      ),
    },
    {
      header: t("Payer (w/o Tax)"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.payer_share_without_tax}
        </span>
      ),
    },
    {
      header: t("Patient"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.patient_share}
        </span>
      ),
    },
    {
      header: t("Net"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.net_price}
        </span>
      ),
    },
    {
      header: t("VAT"),
      render: (row) => {
        const vat = ((row.stats.payer_share_with_tax || 0) * 0.15).toFixed(2);
        return (
          <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
            {vat}
          </span>
        );
      },
    },
    {
      header: t("Items"),
      render: (row) => (
        <span className={row.isTotal ? "font-bold text-gray-800" : ""}>
          {row.stats.item_count}
        </span>
      ),
    },
  ];

  const getGrandTotalColumns = () => [
    {
      header: t("Submitted"),
      render: (row) => (
        <span className="font-bold text-gray-800">{row.submitted_value}</span>
      ),
    },
    {
      header: t("Approved"),
      render: (row) => (
        <span className="font-bold text-gray-800">{row.approved_value}</span>
      ),
    },
    {
      header: t("Payer (w/ Tax)"),
      render: (row) => (
        <span className="font-bold text-gray-800">
          {row.payer_share_with_tax}
        </span>
      ),
    },
    {
      header: t("Payer (w/o Tax)"),
      render: (row) => (
        <span className="font-bold text-gray-800">
          {row.payer_share_without_tax}
        </span>
      ),
    },
    {
      header: t("Patient"),
      render: (row) => (
        <span className="font-bold text-gray-800">{row.patient_share}</span>
      ),
    },
    {
      header: t("Net"),
      render: (row) => (
        <span className="font-bold text-gray-800">{row.net_price}</span>
      ),
    },
    {
      header: t("VAT"),
      render: (row) => (
        <span className="font-bold text-gray-800">
          {row.vat_amount.toFixed(2)}
        </span>
      ),
    },
    {
      header: t("Items"),
      render: (row) => (
        <span className="font-bold text-gray-800">{row.item_count}</span>
      ),
    },
  ];

  const renderReportTables = () => {
    if (!reportData || !reportData.data) return null;

    if (reportData.data.length === 0) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center">
          <p className="text-gray-500 font-medium">{t("No Data Found")}</p>
        </div>
      );
    }

    const grandTotal = {
      submitted_value: 0,
      approved_value: 0,
      payer_share_with_tax: 0,
      payer_share_without_tax: 0,
      patient_share: 0,
      net_price: 0,
      vat_amount: 0,
      item_count: 0,
    };

    return (
      <div className="flex flex-col gap-2">
        {reportData.data.map((monthData, index) => {
          const monthTotal = {
            submitted_value: 0,
            approved_value: 0,
            payer_share_with_tax: 0,
            payer_share_without_tax: 0,
            patient_share: 0,
            net_price: 0,
            vat_amount: 0,
            item_count: 0,
          };

          monthData.financial_stats.forEach((stats) => {
            const s = stats.stats;
            monthTotal.submitted_value += s.submitted_value || 0;
            monthTotal.approved_value += s.approved_value || 0;
            monthTotal.payer_share_with_tax += s.payer_share_with_tax || 0;
            monthTotal.payer_share_without_tax +=
              s.payer_share_without_tax || 0;
            monthTotal.patient_share += s.patient_share || 0;
            monthTotal.net_price += s.net_price || 0;
            monthTotal.vat_amount += (s.payer_share_with_tax || 0) * 0.15;
            monthTotal.item_count += s.item_count || 0;
          });

          // Add to grand total
          Object.keys(grandTotal).forEach((key) => {
            grandTotal[key] += monthTotal[key];
          });

          // Combine raw stats with the monthly total row
          const tableData = [
            ...monthData.financial_stats,
            { isTotal: true, stats: monthTotal },
          ];

          return (
            <div
              key={index}
              className="bg-white border border-slate-200 rounded-lg p-2"
            >
              <div className="mb-2">
                <h2 className="text-sm font-bold text-gray-800">
                  {monthData.year_month} - {monthData.provider_name}
                </h2>
                <h3 className="text-xs text-gray-600 font-medium">
                  {t("Payer")}: {monthData.payer_name}
                </h3>
              </div>
              <AppTable
                columns={getMonthlyColumns()}
                data={tableData}
                actions={false}
              />
            </div>
          );
        })}

        {/* Grand Total Summary */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-2">
          <h2 className="text-sm font-bold text-primary mb-2">
            {t("Grand Total")}
          </h2>
          <AppTable
            columns={getGrandTotalColumns()}
            data={[grandTotal]}
            actions={false}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="pre_auth_container !p-0">
      {/* Filters Form */}
      <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-x-1.5 gap-y-1.5">
          <div className="lg:col-span-2">
            <CustomFilterInput
              label={t("Payer")}
              name="payer_id"
              isSelect
              value={reportPayload.payer_id}
              onChange={handleChange}
              placeholder={t("Select Payer")}
              renderOptions={() => (
                <>
                  <option value="">{t("Select Payer")}</option>
                  {payerDataOptions?.map((category) => (
                    <optgroup
                      key={category.id}
                      label={
                        category?.name_en
                          ? `${category.name_en} (${category.license_id})`
                          : ""
                      }
                    >
                      {category.payers.map((insurance) => (
                        <option key={insurance.id} value={insurance.id}>
                          {insurance.name_en} ({insurance?.license_id})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </>
              )}
            />
          </div>
          <CustomFilterInput
            label={t("StartDate")}
            placeholder={t("Select Start Date")}
            type="month"
            name="start_date"
            value={reportPayload?.start_date}
            onChange={handleChange}
          />
          <CustomFilterInput
            label={t("EndDate")}
            placeholder={t("Select End Date")}
            type="month"
            name="end_date"
            value={reportPayload?.end_date}
            onChange={handleChange}
          />
          <div className="flex items-end justify-end">
            <AppButton
              label={t("View Reports")}
              onClick={handleGetReports}
              disabled={isLoading}
              startIcon={<DoneAllIcon className="!text-[12px]" />}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div className="flex items-center justify-center p-10">
            <div className="animate-pulse text-gray-500">{t("Loading...")}</div>
          </div>
        ) : (
          renderReportTables()
        )}
      </div>
    </div>
  );
};

export default ReportSummary;
